import React, { useState, Fragment, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ChartPieIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import {
  calculatePortfolioGain,
  calculateReturnPercentage,
  convertCurrency,
} from '../../utils/calculations';
import {
  formatCurrency,
  formatCurrencyWithSign,
  formatPercentageWithSign,
} from '../../utils/formatters';

const Accounts = () => {
  const { portfolios, goals, settings, exchangeRates, deletePortfolio } = useData();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleDelete = (accountId) => {
    deletePortfolio(accountId);
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  // Calculate total value in display currency
  const totalValueInDisplayCurrency = portfolios.reduce((sum, portfolio) => {
    const converted = convertCurrency(
      portfolio.currentValue,
      portfolio.currency,
      settings.displayCurrency,
      exchangeRates
    );
    return sum + converted;
  }, 0);

  // Handle sort column change
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort portfolios based on selected column and direction
  const sortedPortfolios = useMemo(() => {
    const sorted = [...portfolios].map(portfolio => {
      // Pre-calculate values needed for sorting
      const totalGain = calculatePortfolioGain(portfolio.currentValue, portfolio.costBasis);

      // Calculate allocated amount to goals
      let allocatedAmount = 0;
      goals.forEach(goal => {
        if (goal.allocations) {
          goal.allocations.forEach(allocation => {
            if (allocation.portfolioId === portfolio.id) {
              if (allocation.type === 'value' || allocation.type === 'amount') {
                allocatedAmount += allocation.value || 0;
              } else if (!allocation.type || allocation.type === 'percentage') {
                const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage;
                allocatedAmount += (portfolio.currentValue * (percentValue / 100));
              }
            }
          });
        }
      });
      const unallocatedAmount = portfolio.currentValue - allocatedAmount;

      return {
        ...portfolio,
        _totalGain: totalGain,
        _unallocatedAmount: unallocatedAmount,
      };
    });

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);

        case 'type':
          aValue = a.type.toLowerCase();
          bValue = b.type.toLowerCase();
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);

        case 'currentValue':
          aValue = a.currentValue || 0;
          bValue = b.currentValue || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;

        case 'gain':
          aValue = a._totalGain || 0;
          bValue = b._totalGain || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;

        case 'available':
          aValue = a._unallocatedAmount || 0;
          bValue = b._unallocatedAmount || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;

        case 'monthlyContribution':
          aValue = a.monthlyContribution || 0;
          bValue = b.monthlyContribution || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;

        default:
          return 0;
      }
    });

    return sorted;
  }, [portfolios, sortColumn, sortDirection, goals]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight m-0 mb-2">
            Portfolio Accounts
          </h1>
          <div className="flex items-center gap-3 text-base text-gray-600">
            <span>Total Value:</span>
            <span className="text-xl text-gray-900 font-bold tabular-nums">
              {formatCurrency(totalValueInDisplayCurrency, settings.displayCurrency, 0)}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/accounts/new')}
            className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg font-medium transition-colors shadow-sm"
            style={{ backgroundColor: '#012E58' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Account</span>
          </button>
        </div>
      </div>

      {portfolios.length === 0 ? (
        <div className="mt-6 text-center py-12 px-6">
          <p className="text-gray-600 mb-4">
            No accounts created yet
          </p>
          <button
            onClick={() => navigate('/accounts/new')}
            className="px-5 py-2.5 text-white rounded-lg font-medium transition-colors shadow-sm"
            style={{ backgroundColor: '#012E58' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
          >
            Create Your First Account
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Account</span>
                      {sortColumn === 'name' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Type</span>
                      {sortColumn === 'type' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('currentValue')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Current Value</span>
                      {sortColumn === 'currentValue' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('gain')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Gain/Loss</span>
                      {sortColumn === 'gain' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('available')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Available for Allocation</span>
                      {sortColumn === 'available' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('monthlyContribution')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Monthly Contrib.</span>
                      {sortColumn === 'monthlyContribution' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-1 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPortfolios.map((portfolio) => {
                  const totalGain = calculatePortfolioGain(portfolio.currentValue, portfolio.costBasis);
                  const returnPercentage = calculateReturnPercentage(portfolio.currentValue, portfolio.costBasis);
                  const valueInDisplayCurrency = convertCurrency(
                    portfolio.currentValue,
                    portfolio.currency,
                    settings.displayCurrency,
                    exchangeRates
                  );
                  const isPositive = totalGain >= 0;

                  // Calculate allocated amount to goals
                  let allocatedAmount = 0;
                  goals.forEach(goal => {
                    if (goal.allocations) {
                      goal.allocations.forEach(allocation => {
                        if (allocation.portfolioId === portfolio.id) {
                          if (allocation.type === 'value' || allocation.type === 'amount') {
                            allocatedAmount += allocation.value || 0;
                          } else if (!allocation.type || allocation.type === 'percentage') {
                            const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage;
                            allocatedAmount += (portfolio.currentValue * (percentValue / 100));
                          }
                        }
                      });
                    }
                  });
                  const unallocatedAmount = portfolio.currentValue - allocatedAmount;

                  return (
                    <tr
                      key={portfolio.id}
                      onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-1">
                        <div className="flex items-center gap-2">
                          <ChartPieIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-semibold text-gray-900">{portfolio.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-1">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                          {portfolio.type}
                        </span>
                      </td>
                      <td className="px-4 py-1 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="text-sm font-bold text-gray-900 tabular-nums">
                            {formatCurrency(portfolio.currentValue, portfolio.currency, 0)}
                          </div>
                          {portfolio.currency !== settings.displayCurrency && (
                            <div className="text-xs text-gray-500 tabular-nums">
                              ≈ {formatCurrency(valueInDisplayCurrency, settings.displayCurrency, 0)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <div className={`text-sm font-semibold tabular-nums ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrencyWithSign(totalGain, portfolio.currency, 0)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatPercentageWithSign(returnPercentage)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-1 text-right">
                        <div className="text-sm text-gray-900 tabular-nums">
                          {formatCurrency(unallocatedAmount, portfolio.currency, 0)}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-right">
                        <div className="text-sm font-medium text-gray-900 tabular-nums">
                          {formatCurrency(portfolio.monthlyContribution || 0, portfolio.currency, 0)}
                        </div>
                      </td>
                      <td className="px-4 py-1">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/accounts/edit/${portfolio.id}`);
                            }}
                            className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <PencilIcon className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAccountToDelete(portfolio);
                              setDeleteDialogOpen(true);
                            }}
                            className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Delete this account?
                    </Dialog.Title>
                  </div>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This will delete all associated pots and transactions. This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setAccountToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      onClick={() => accountToDelete && handleDelete(accountToDelete.id)}
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Accounts;
